import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  ButtonBase,
  Collapse,
  Fade,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";

import { formatPricePerCarat } from "../../../tools";
import ColorLink from "../../widgets/ColorLink";
import SectionTitle from "../../widgets/SectionTitle";
import { PricingTrendItem } from "./graphql";

type ColorFactor = {
  color: string;
  langermanFactor: number;
  competitorFactor: number;
  marketValue: number;
};

const GAP_STOPS: [number, string][] = [
  [-0.3, "#d03b3b"],
  [-0.2, "#f08c2e"],
  [-0.1, "#e8c93a"],
  [0, "#2e9e5b"],
  [0.15, "#2a9d9b"],
  [0.3, "#2f6fd0"],
];
const GAP_LO = GAP_STOPS[0][0];
const GAP_HI = GAP_STOPS[GAP_STOPS.length - 1][0];

export function latestColorFactors(items: PricingTrendItem[]): ColorFactor[] {
  return items
    .filter((item) => item.color.valid)
    .map((item) => ({
      color: item.color.color,
      langermanFactor: item.langermanFactor,
      competitorFactor: item.competitorFactor,
      marketValue: item.marketValue,
    }));
}

function primaryOf(color: string): string {
  const last = color.trim().split(/\s+/).at(-1) ?? "";
  return last === "Red" ? "Pink" : last;
}

// The longest intensity the name starts with, so "Fancy Intense Pink" doesn't stop at "Fancy": the
// first match in a list sorted longest first.
function splitColor(
  color: string,
  intensitiesLongestFirst: string[],
): {
  intensity: string | null;
  nuance: string;
} {
  const name = color.trim();
  const intensity = intensitiesLongestFirst.find((p) =>
    name.startsWith(`${p} `),
  );
  if (!intensity) {
    return { intensity: null, nuance: name };
  }
  return { intensity, nuance: name.slice(intensity.length).trim() };
}

const hexToRgb = (hex: string) =>
  [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

function gapHex(gap: number): string {
  if (gap <= GAP_LO) {
    return GAP_STOPS[0][1];
  }
  if (gap >= GAP_HI) {
    return GAP_STOPS[GAP_STOPS.length - 1][1];
  }
  const upper = GAP_STOPS.findIndex(([x]) => x >= gap);
  const [x0, c0] = GAP_STOPS[upper - 1];
  const [x1, c1] = GAP_STOPS[upper];
  const t = (gap - x0) / (x1 - x0);
  const a = hexToRgb(c0);
  const b = hexToRgb(c1);
  return (
    "#" +
    a
      .map((v, i) =>
        Math.round(v + (b[i] - v) * t)
          .toString(16)
          .padStart(2, "0"),
      )
      .join("")
  );
}

function formatGap(gap: number): string {
  const pct = Math.round(gap * 100);
  if (pct === 0) {
    return "0%";
  }
  return `${pct > 0 ? "+" : "−"}${Math.abs(pct)}%`;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

const stopPosition = (x: number) =>
  `${((x - GAP_LO) / (GAP_HI - GAP_LO)) * 100}%`;

const CELL_W = 88;
const CELL_H = 52;

const lift = {
  transform: "translateY(-2px)",
  boxShadow: "0 4px 12px rgba(0,0,0,.15)",
};

const cellSx: SxProps<Theme> = {
  width: CELL_W,
  height: CELL_H,
  boxSizing: "border-box",
  p: 0.5,
  borderRadius: "6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  transition: "transform .15s ease, box-shadow .15s ease",
};

const columnLabelSx: SxProps<Theme> = {
  fontSize: "0.6875rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "text.secondary",
  textAlign: "center",
};

const TooltipLines = ({ lines }: { lines: string[] }) => (
  <Stack>
    {lines.map((line, i) => (
      <Typography
        key={line}
        variant="caption"
        fontWeight={i === 0 ? "bold" : undefined}
      >
        {line}
      </Typography>
    ))}
  </Stack>
);

// The gap's color is carried by its figure, on a plain framed cell. A cell standing for one GIA
// color is a link to that color's pricing, in a new tab, as a color name is anywhere else.
const GapCell = ({
  gap,
  lines,
  pricingColor,
}: {
  gap: number;
  lines: string[];
  pricingColor?: string;
}) => {
  const figureSx = { fontSize: 15, fontWeight: 600, color: gapHex(gap) };
  return (
    <Tooltip title={<TooltipLines lines={lines} />} arrow placement="top">
      <Box
        className="gap-cell"
        sx={{
          ...cellSx,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        {pricingColor ? (
          <ColorLink
            color={pricingColor}
            label={formatGap(gap)}
            style={figureSx}
          />
        ) : (
          <Typography component="span" sx={figureSx}>
            {formatGap(gap)}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const EmptyCell = () => (
  <Box
    sx={{
      ...cellSx,
      border: "1px dashed",
      borderColor: "divider",
      color: "text.secondary",
    }}
  >
    —
  </Box>
);

const Legend = () => (
  <Box sx={{ mb: 2, maxWidth: 520 }}>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        <ChevronLeftIcon fontSize="inherit" />
        Langerman cheaper
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        Langerman more expensive
        <ChevronRightIcon fontSize="inherit" />
      </Typography>
    </Stack>
    <Box
      sx={{
        height: 10,
        borderRadius: 5,
        background: `linear-gradient(to right, ${GAP_STOPS.map(
          ([x, c]) => `${c} ${stopPosition(x)}`,
        ).join(", ")})`,
      }}
    />
    <Box sx={{ position: "relative", height: 16, mt: 0.5 }}>
      {GAP_STOPS.map(([x]) => (
        <Typography
          key={x}
          variant="caption"
          color="text.secondary"
          sx={{
            position: "absolute",
            left: stopPosition(x),
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
          }}
        >
          {formatGap(x)}
        </Typography>
      ))}
    </Box>
  </Box>
);

// One entry per row of a primary's grid, the colors of every intensity of that nuance together.
function groupByNuance(
  rows: ColorFactor[],
  intensitiesLongestFirst: string[],
): Map<string, ColorFactor[]> {
  const groups = new Map<string, ColorFactor[]>();
  for (const row of rows) {
    const { intensity, nuance } = splitColor(
      row.color,
      intensitiesLongestFirst,
    );
    if (!intensity) {
      continue;
    }
    groups.set(nuance, [...(groups.get(nuance) ?? []), row]);
  }
  return groups;
}

const colorCount = (n: number) => `${n} color${n !== 1 ? "s" : ""}`;

const colorLines = (row: ColorFactor) => [
  row.color,
  `Langerman factor ${row.langermanFactor.toFixed(3)}`,
  `Competitor factor ${row.competitorFactor.toFixed(3)}`,
  `Market value ${formatPricePerCarat(row.marketValue)}`,
];

const IntensityCells = ({
  intensities,
  intensitiesLongestFirst,
  colors,
}: {
  intensities: string[];
  intensitiesLongestFirst: string[];
  colors: ColorFactor[];
}) => {
  const byIntensity = new Map(
    colors.map((c) => [
      splitColor(c.color, intensitiesLongestFirst).intensity,
      c,
    ]),
  );
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${intensities.length}, ${CELL_W}px)`,
        gap: 0.5,
        mt: 0.5,
        mb: 1,
        ml: 3,
        pl: 1.5,
        borderLeft: 1,
        borderColor: "divider",
        "& .gap-cell:hover": lift,
      }}
    >
      {intensities.map((intensity) => (
        <Typography key={intensity} sx={columnLabelSx}>
          {intensity}
        </Typography>
      ))}
      {intensities.map((intensity) => {
        const row = byIntensity.get(intensity);
        return row ? (
          <GapCell
            key={intensity}
            gap={row.langermanFactor / row.competitorFactor - 1}
            lines={colorLines(row)}
            pricingColor={row.color}
          />
        ) : (
          <EmptyCell key={intensity} />
        );
      })}
    </Box>
  );
};

const NuanceRow = ({
  nuance,
  colors,
  intensities,
  intensitiesLongestFirst,
}: {
  nuance: string;
  colors: ColorFactor[];
  intensities: string[];
  intensitiesLongestFirst: string[];
}) => {
  const [open, setOpen] = useState(false);
  const langermanFactor = median(colors.map((c) => c.langermanFactor));
  const competitorFactor = median(colors.map((c) => c.competitorFactor));

  return (
    <Box>
      <ButtonBase
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        sx={{
          gap: 1,
          borderRadius: "6px",
          justifyContent: "flex-start",
          "&:hover .gap-cell": lift,
        }}
      >
        <GapCell
          gap={langermanFactor / competitorFactor - 1}
          lines={[
            `${nuance} · ${colorCount(colors.length)}`,
            `Median Langerman factor ${langermanFactor.toFixed(3)}`,
            `Median competitor factor ${competitorFactor.toFixed(3)}`,
          ]}
        />
        <Typography variant="body2" sx={{ minWidth: 140, textAlign: "left" }}>
          {nuance}
        </Typography>
        {open ? (
          <ExpandMoreIcon fontSize="small" color="action" />
        ) : (
          <ChevronRightIcon fontSize="small" color="action" />
        )}
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <IntensityCells
          intensities={intensities}
          intensitiesLongestFirst={intensitiesLongestFirst}
          colors={colors}
        />
      </Collapse>
    </Box>
  );
};

const NuanceGrid = ({
  primary,
  rows,
  allIntensities,
  intensitiesLongestFirst,
}: {
  primary: string;
  rows: ColorFactor[];
  allIntensities: string[];
  intensitiesLongestFirst: string[];
}) => {
  const groups = groupByNuance(rows, intensitiesLongestFirst);
  const present = new Set(
    Array.from(groups.values())
      .flat()
      .map((row) => splitColor(row.color, intensitiesLongestFirst).intensity),
  );
  const intensities = allIntensities.filter((i) => present.has(i));
  const nuances = Array.from(groups.keys()).sort((a, b) =>
    a === primary ? -1 : b === primary ? 1 : a.localeCompare(b),
  );

  return (
    <Box>
      <SectionTitle>
        {primary}
        {primary === "Pink" ? " (incl. Red)" : ""}
      </SectionTitle>
      <Stack gap={0.5}>
        {nuances.map((nuance) => (
          <NuanceRow
            key={nuance}
            nuance={nuance}
            colors={groups.get(nuance) ?? []}
            intensities={intensities}
            intensitiesLongestFirst={intensitiesLongestFirst}
          />
        ))}
      </Stack>
    </Box>
  );
};

// The intensities come weakest first and the primaries in the order their buttons show.
const FactorMatrix = ({
  rows,
  intensities,
  primaries: primaryOrder,
}: {
  rows: ColorFactor[];
  intensities: string[];
  primaries: string[];
}) => {
  const [selected, setSelected] = useState<string | null>(null);

  const intensitiesLongestFirst = useMemo(
    () => [...intensities].sort((a, b) => b.length - a.length),
    [intensities],
  );

  const byPrimary = useMemo(() => {
    const map = new Map<string, ColorFactor[]>();
    for (const row of rows) {
      const primary = primaryOf(row.color);
      const group = map.get(primary);
      if (group) {
        group.push(row);
      } else {
        map.set(primary, [row]);
      }
    }
    return map;
  }, [rows]);

  const primaries = useMemo(() => {
    const listed = new Set(primaryOrder);
    return [
      ...primaryOrder.filter((p) => byPrimary.has(p)),
      ...Array.from(byPrimary.keys())
        .filter((p) => !listed.has(p))
        .sort(),
    ];
  }, [byPrimary, primaryOrder]);
  return (
    <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
      <Legend />
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        {primaries.map((primary) => {
          const colors = byPrimary.get(primary) ?? [];
          const gap =
            median(colors.map((c) => c.langermanFactor / c.competitorFactor)) -
            1;
          const isSelected = selected === primary;
          return (
            <ButtonBase
              key={primary}
              aria-pressed={isSelected}
              onClick={() => setSelected(isSelected ? null : primary)}
              sx={{
                ...cellSx,
                flexDirection: "column",
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
                "&:hover": lift,
                ...(isSelected && {
                  transform: "translateY(-2px)",
                  boxShadow: ({ palette }: Theme) =>
                    `0 0 0 2px ${palette.background.paper}, ` +
                    `0 0 0 4px ${palette.text.primary}`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }),
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                }}
              >
                {primary}
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: 15, fontWeight: 600, color: gapHex(gap) }}
              >
                {formatGap(gap)}
              </Typography>
            </ButtonBase>
          );
        })}
      </Stack>

      {selected && byPrimary.has(selected) && (
        <Fade key={selected} in timeout={250}>
          <Box>
            <NuanceGrid
              primary={selected}
              rows={byPrimary.get(selected) ?? []}
              allIntensities={intensities}
              intensitiesLongestFirst={intensitiesLongestFirst}
            />
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default FactorMatrix;
